import { useRef } from "react";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { GiftCards as GiftCardsType } from "lib/types/checkout";
import { GiftCardAppliedCardType, GiftCardProviderType } from "lib/types/giftCards";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { inIframe } from "lib/utils/helpers";
import useSendAnalyticsEvent from "./useAnalytics";
import useGiftCards from "./useGiftCards";

type GiftCardCardType = { provider_id?: string };

type GiftCardValidationPayload = { provider: GiftCardProviderType; card_number?: string };

/** One entry per tracked event; add the next event's type and payload here. */
type GiftCardEventType =
  | { type: "MODAL_OPENED" }
  | { type: "VALIDATION_INITIATED"; payload: GiftCardValidationPayload }
  | { type: "MODAL_CLOSED" }
  | { type: "REMOVED"; payload: { card: GiftCardAppliedCardType; surface: "modal" | "checkout" } }
  | { type: "APPLIED"; payload: GiftCardValidationPayload & { response: any } }
  | { type: "VALIDATION_FAILED"; payload: GiftCardValidationPayload & { error: unknown } }
  | { type: "VALIDATION_SUCCESS"; payload: GiftCardValidationPayload & { balance: number; latency_ms: number } };

/** GYFTR is the only wallet-shaped provider today; the rest are cards. */
const getCardType = (provider: GiftCardProviderType) => (provider === "GYFTR" ? "wallet" : "card");

/**
 * Reads the failure off whatever the request threw. The backend's own code is passed through as is;
 * the category is a best-effort bucket over it and the message, with no response at all meaning the
 * request timed out or never landed.
 */
const getValidationError = (error: any) => {
  // The error body is `{ success: false, error: { code, message, data } }`; `error.code` is axios' own
  // (e.g. a timeout) and only stands in when the body has none.
  const body = error?.response?.data?.error;
  const code = body?.code ?? error?.code;
  const text = `${code ?? ""} ${body?.message ?? ""}`.toLowerCase();

  let category = "invalid";
  if (!error?.response || /timeout|timed out|econnaborted/.test(text)) category = "timeout";
  else if (/expir/.test(text)) category = "expired";
  else if (/deactivat|disabled|inactive|blocked/.test(text)) category = "deactivated";
  else if (/lock|too many|attempts/.test(text)) category = "pin_lockout";
  else if (/insufficient|zero balance|no balance/.test(text)) category = "insufficient_balance";

  return { error_code: code ?? null, error_category: category };
};

// Keyed by provider + number so retyping the same card is the same card while another starts over.
// Only ever held in memory; the number itself is never sent.
const getCardKey = ({ provider, card_number }: GiftCardValidationPayload) => `${provider}:${card_number ?? ""}`;

const getCardIdentity = ({ provider }: GiftCardValidationPayload): GiftCardCardType => ({
  provider_id: provider,
});

/**
 * Funnel events for the gift card pop-up. Every `gc_` event carries the same identifying fields,
 * so they are assembled here once and each tracker only adds what is specific to its event.
 *
 * `provider_id` is null until the shopper has picked a provider. The card number and PIN are never
 * sent; the number is only used in memory to tell one card's attempts from another's.
 */
const useGiftCardAnalytics = () => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: { checkoutId, giftCards },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { giftCardConfig },
  } = useGiftCards();

  const sendGiftCardEvent = (
    eventName: string,
    properties: Record<string, unknown>,
    card?: GiftCardCardType,
  ) =>
    sendAnalyticsEvent({
      eventName,
      eventFor: [analyticsTypes.SF_ANALYTICS],
      eventType: "click",
      metaData: {
        giftCardData: {
          merchant_id: merchant?.merchantId,
          checkout_id: checkoutId,
          checkout_mode: inIframe() ? "brand_domain" : "redirect",
          provider_id: card?.provider_id ?? null,
          ...properties,
        },
      },
    });

  // What happened since the pop-up last opened, for `gc_modal_closed`. A ref because it is read only
  // when the pop-up closes. `isOpen` also keeps a second close call from reporting the same session.
  const modalSession = useRef({ isOpen: false, cardsApplied: 0, lastErrorCategory: null as string | null });

  const trackModalOpened = () => {
    modalSession.current = { isOpen: true, cardsApplied: 0, lastErrorCategory: null };
    sendGiftCardEvent(analyticsEvents.FLO_GC_MODAL_OPENED, {
      cards_applied_count: giftCards?.length ?? 0,
      // Every provider the backend offers this checkout, in the order the pop-up lists them.
      provider_list: giftCardConfig.providers.map(({ provider }) => provider),
    });
  };

  const trackModalClosed = () => {
    const { isOpen, cardsApplied, lastErrorCategory } = modalSession.current;
    if (!isOpen) return;
    modalSession.current.isOpen = false;
    sendGiftCardEvent(analyticsEvents.FLO_GC_MODAL_CLOSED, {
      outcome: cardsApplied > 0 ? "applied" : "abandoned",
      cards_applied_in_session: cardsApplied,
      last_error_category: lastErrorCategory,
    });
  };

  const attemptCounts = useRef<Record<string, number>>({});

  const trackValidationInitiated = (payload: GiftCardValidationPayload) => {
    const cardKey = getCardKey(payload);
    const attemptNo = (attemptCounts.current[cardKey] ?? 0) + 1;
    attemptCounts.current[cardKey] = attemptNo;
    const { provider } = payload;

    sendGiftCardEvent(
      analyticsEvents.FLO_GC_VALIDATION_INITIATED,
      {
        card_type: getCardType(provider),
        // Position of the provider in the list the pop-up renders.
        card_index: giftCardConfig.providers.findIndex(({ provider: configured }) => configured === provider),
        attempt_no: attemptNo,
      },
      getCardIdentity(payload),
    );
  };

  // `response` is the checkout the apply call returned. The card that just landed is the one whose
  // transaction wasn't on the checkout before it.
  const trackApplied = (payload: GiftCardValidationPayload & { response: any }) => {
    modalSession.current.cardsApplied += 1;
    const appliedCards: GiftCardsType[] = payload.response?.gift_cards ?? [];
    const previousTransactionIds = new Set((giftCards ?? []).map((card) => card.transaction_id));
    const newCard = appliedCards.find(
      (card) => card.transaction_id && !previousTransactionIds.has(card.transaction_id),
    );

    sendGiftCardEvent(
      analyticsEvents.FLO_GC_APPLIED,
      {
        amount_applied: newCard?.amount ?? 0,
        remaining_payable: payload.response?.pricing?.total_payable ?? 0,
        cards_applied_count: appliedCards.length,
        // Across every card now on the checkout, the one just applied included.
        total_gc_amount_applied: appliedCards.reduce((total, card) => total + (card.amount ?? 0), 0),
      },
      getCardIdentity(payload),
    );
  };

  const trackRemoved = ({
    card,
    surface,
  }: Extract<GiftCardEventType, { type: "REMOVED" }>["payload"]) =>
    sendGiftCardEvent(
      analyticsEvents.FLO_GC_REMOVED,
      {
        provider_name: card.provider ?? null,
        amount_removed: card.amount,
        surface,
      },
      { provider_id: card.provider },
    );

  const trackValidationFailed = (payload: GiftCardValidationPayload & { error: unknown }) => {
    const validationError = getValidationError(payload.error);
    modalSession.current.lastErrorCategory = validationError.error_category;
    sendGiftCardEvent(
      analyticsEvents.FLO_GC_VALIDATION_FAILED,
      {
        ...validationError,
        // The attempt that just failed, counted when it was initiated.
        attempt_no: attemptCounts.current[getCardKey(payload)] ?? 1,
      },
      getCardIdentity(payload),
    );
  };

  const trackValidationSuccess = (payload: GiftCardValidationPayload & { balance: number; latency_ms: number }) =>
    sendGiftCardEvent(
      analyticsEvents.FLO_GC_VALIDATION_SUCCESS,
      { balance: payload.balance, latency_ms: payload.latency_ms },
      getCardIdentity(payload),
    );

  const trackGiftCardEvent = (event: GiftCardEventType) => {
    switch (event.type) {
      case "MODAL_OPENED":
        return trackModalOpened();
      case "VALIDATION_INITIATED":
        return trackValidationInitiated(event.payload);
      case "MODAL_CLOSED":
        return trackModalClosed();
      case "REMOVED":
        return trackRemoved(event.payload);
      case "APPLIED":
        return trackApplied(event.payload);
      case "VALIDATION_FAILED":
        return trackValidationFailed(event.payload);
      case "VALIDATION_SUCCESS":
        return trackValidationSuccess(event.payload);
    }
  };

  return { trackGiftCardEvent };
};

export default useGiftCardAnalytics;
